import {
  Box,
  Button,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash-es";
import { useCallback, useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import ViewContentBlockHeader from "@/App/Layouts/ViewContentBlockHeader";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppIconButton from "@/common/components/Button/AppIconButton";
import AppChip from "@/common/components/Chip/AppChip";
import DateRangeDuration from "@/common/components/Datetime/DateRangeDuration";
import Datetime from "@/common/components/Datetime/Datetime";
import DatetimeRange from "@/common/components/Datetime/DatetimeRange";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import TimeSpanDisplay from "@/common/components/Form/Display/TimeSpanDisplay";
import GeneralStageHistory from "@/common/components/GeneralStageHistory";
import AllocationStatusIcon from "@/common/components/Icons/AllocationStatusIcon";
import AppIcon from "@/common/components/Icons/AppIcon";
import { OneflowContractRefDisplayModal } from "@/common/components/Integrations/Oneflow/OneflowContractRefDisplayModal";
import AppLink from "@/common/components/Link/AppLink";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import AppTypography from "@/common/components/Text/AppTypography";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { FileItem } from "@/common/fileItem";
import { noop } from "@/common/helpers/function";
import { OneflowHelper } from "@/common/helpers/integrations/oneflow";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserProfileV2 } from "@/common/hooks/user/useUserProfileV2";
import { ViewVariant } from "@/common/ts/layout";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  AssetSubscriptionDto,
  ContractDto,
  ContractStage,
  ContractType,
  EntityType,
  UnitOfTime,
} from "@/core/api/generated/v1";

import AccessoryGrid from "../../../Accessory/AccessoryGrid";
import AssetSubscriptionLink from "../../../AssetSubscription/AssetSubscriptionLink";
import CustomerLink from "../../../Customer/CustomerLink";
import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import VehicleLink from "../../../Vehicle/VehicleLink";
import { ContractVisualInspectionComparisonModal } from "../../Comparison/ContractVisualInspectionComparisonModal";
import ContractCustomerCommunicationInfo from "../../ContractCustomerCommunicationInfo";
import ContractCustomerCommunicationInfoModal from "../../ContractCustomerCommunicationInfoModal";
import ContractReminderSettingsDisplay from "../../ContractReminderSettingsDisplay";
import ContractSettingsDisplay from "../../ContractSettingsDisplay";
import ContractSpotInfoDisplay from "../../ContractSpotInfoDisplay";
import ContractUpdateSpotInfoModal from "../../ContractUpdateSpotInfoModal";
import CreateContractCustomerCommunicationEntryModal from "../../CreateContractCustomerCommunicationEntryModal";
import { ContractCreateOneflowContractModal } from "../../Oneflow/ContractCreateOneflowContractModal";

interface Props {
  viewVariant?: ViewVariant;
  contract: ContractDto;
  assetSubscription: AssetSubscriptionDto | null | undefined;
  onUpdate: (newValue?: ContractDto) => void;
  onReallocateVehicle: () => void;
}

export default function OverviewTabContent({
  contract,
  assetSubscription,
  viewVariant = ViewVariant.Tab,
  onUpdate,
  onReallocateVehicle,
}: Props) {
  const { enqueueSnackbar } = useAppSnackbar();
  const profile = useUserProfileV2();

  const [isReallocateVehicleModalOpenLoading] = useState(false);
  const [isCommunicationInfoModalOpen, setIsCommunicationInfoModalOpen] = useState(false);
  const [isCreateCommunicationEntryModalOpen, setIsCreateCommunicationEntryModalOpen] =
    useState(false);
  const [isEditCheckOutSpotInfoModalOpen, setIsEditCheckOutSpotInfoModalOpen] = useState(false);
  const [isEditCheckInSpotInfoModalOpen, setIsEditCheckInSpotInfoModalOpen] = useState(false);
  const [isCompareVisualInspectionsModalOpen, setIsCompareVisualInspectionsModalOpen] =
    useState(false);

  const handleUpdateStage = useCallback(
    async (newStage: ContractStage) => {
      try {
        const response = await apiClient.contractsApi.apiV1ContractsContractIdStagePut({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          contractId: contract.id!,
          contractStageUpdateDto: {
            newStage,
          },
        });
        onUpdate(response.data);
      } catch (err) {
        const validation2 = ValidationHelper.handleApiErrorResponse(err);
        validation2.hasErrors &&
          enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
      }
    },
    [contract],
  );

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={contract} />}
          />
        }
      >
        <ViewContentBlock>
          <Stack gap={1}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                // alignItems: "start",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <FieldValue
                variant='filled'
                direction='column'
                label='Vehicle'
                isEmpty={!contract?.vehicle}
              >
                <Stack>
                  <VehicleLink entity={contract?.vehicle} />
                  <Box>
                    <AllocationStatusIcon status={contract.allocationStatus} />{" "}
                    <InlineApiEnumValue
                      type='AllocationStatus'
                      value={contract.allocationStatus}
                      withHelperTooltip
                    />{" "}
                    {contract?.canReallocate && (
                      <AppTooltip title='Reallocate vehicle' isInline>
                        <AppIconButton
                          size='small'
                          loading={isReallocateVehicleModalOpenLoading}
                          onClick={() => onReallocateVehicle && onReallocateVehicle()}
                        >
                          <AppIcon of='edit' />
                        </AppIconButton>
                      </AppTooltip>
                    )}
                  </Box>
                </Stack>
              </FieldValue>

              <FieldValue
                variant='filled'
                direction='column'
                label='Customer'
                isEmpty={!contract?.customer}
              >
                <CustomerLink entity={contract?.customer} />
              </FieldValue>

              {contract.type === ContractType.Subscription && (
                <FieldValue
                  variant='filled'
                  direction='column'
                  label='Asset subscription'
                  isEmpty={!contract?.assetSubscription}
                >
                  <AssetSubscriptionLink entity={contract?.assetSubscription} />
                </FieldValue>
              )}

              <FieldValue
                variant='filled'
                direction='column'
                label='External number'
                isEmpty={!contract.externalNumber}
                endAdornment={
                  <BaseEditExternalNumberModal
                    initialValues={{ externalNumber: contract.externalNumber }}
                    onConfirm={async (externalNumber) => {
                      const response =
                        await apiClient.contractsApi.apiV1ContractsContractIdExternalNumberPut({
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          contractId: contract.id || "",
                          generalExternalNumberInputDto: { externalNumber },
                        });
                      onUpdate(response.data);
                    }}
                  />
                }
              >
                {contract.externalNumber}
              </FieldValue>

              <FieldValue variant='filled' direction='column' label='Type'>
                <InlineApiEnumValue type='ContractType' value={contract.type} />
              </FieldValue>

              <FieldValue
                variant='filled'
                direction='column'
                label='Start & end'
                isEmpty={!contract.startsAt}
              >
                <DatetimeRange
                  range={{ startsAt: contract.startsAt, endsAt: contract.endsAt }}
                  durationHumanizeOptions={{
                    maxUnit: UnitOfTime.Day,
                  }}
                  variant='normal'
                  direction='row'
                  withDuration
                  withTooltip
                />
              </FieldValue>

              {contract.isBuffered && (
                <FieldValue variant='filled' direction='column' label='Effective start & end'>
                  <AppTypography
                    component='span'
                    decoration={{ variant: "helpText" }}
                    tooltipProps={{
                      title: (
                        <Typography component='div' variant='body2' sx={{ whiteSpace: "nowrap" }}>
                          <Stack>
                            <Box>
                              Start: <Datetime datetime={contract.startsAtEffective} />
                            </Box>
                            <Box>
                              End: <Datetime datetime={contract.endsAtEffective} />
                            </Box>
                            <Box>
                              Buffer before:{" "}
                              {contract.bufferBefore ? (
                                <TimeSpanDisplay value={contract.bufferBefore} />
                              ) : (
                                "-"
                              )}
                            </Box>
                            <Box>
                              Buffer after:{" "}
                              {contract.bufferAfter ? (
                                <TimeSpanDisplay value={contract.bufferAfter} />
                              ) : (
                                "-"
                              )}
                            </Box>
                            <Box>
                              Duration:{" "}
                              <DateRangeDuration
                                range={{
                                  startsAt: contract.startsAtEffective,
                                  endsAt: contract.endsAtEffective,
                                }}
                                humanizeOptions={{
                                  maxUnit: UnitOfTime.Day,
                                }}
                                withTooltip={false}
                              />
                            </Box>
                          </Stack>
                        </Typography>
                      ),
                    }}
                  >
                    <DatetimeRange
                      range={{
                        startsAt: contract.startsAtEffective,
                        endsAt: contract.endsAtEffective,
                      }}
                      durationHumanizeOptions={{
                        maxUnit: UnitOfTime.Day,
                      }}
                      direction='row'
                      withDuration
                      withTooltip={false}
                    />
                  </AppTypography>
                </FieldValue>
              )}

              {(contract.stageHistory?.stage === ContractStage.Reserved ||
                contract.stageHistory?.stage === ContractStage.ReservationExpired) && (
                <FieldValue
                  variant='filled'
                  direction='column'
                  label='Reservation expires at'
                  isEmpty={!contract.reservationExpiresAt}
                >
                  <Datetime datetime={contract.reservationExpiresAt} withDurationFromNow />
                </FieldValue>
              )}

              {/* <FieldValue
                    variant='filled'
                    direction='column'
                    label='Starts at'
                    isEmpty={!contract.startsAt}
                  >
                    <Datetime datetime={contract.startsAt} withDurationFromNow />
                  </FieldValue>

                  <FieldValue
                    variant='filled'
                    direction='column'
                    label='Ends at'
                    isEmpty={!contract.endsAt}
                  >
                    <Datetime datetime={contract.endsAt} withDurationFromNow />
                  </FieldValue>

                  <FieldValue
                    variant='filled'
                    direction='column'
                    label='Duration'
                    isEmpty={!contract.startsAt && !contract.endsAt}
                  >
                    {`${durationInDays} ${TextHelper.pluralize("days", durationInDays || 0)}`}
                  </FieldValue> */}

              <FieldValue
                variant='filled'
                direction='column'
                label='Notes'
                isEmpty={!contract.notes}
                endAdornment={
                  !contract.oneflowContract && (
                    <BaseEditNotesModal
                      initialValues={{ notes: contract.notes }}
                      onConfirm={async (notes) => {
                        const response =
                          await apiClient.contractsApi.apiV1ContractsContractIdNotesPut({
                            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                            contractId: contract?.id || "",
                            generalNotesInputDto: { notes },
                          });
                        onUpdate(response.data);
                      }}
                    />
                  )
                }
              >
                {contract.notes}
              </FieldValue>

              {OneflowHelper.isFeatureEnabledForTenantUser(profile.profile) && (
                <FieldValue
                  variant='filled'
                  direction='column'
                  label='Oneflow contract'
                  // isEmpty={!contract.oneflowContract}
                  endAdornment={
                    !contract.oneflowContract ? (
                      <ContractCreateOneflowContractModal
                        trigger={
                          <AppIconButton
                            variant='text'
                            size='small'
                            color='default'
                            tooltipProps={{ title: "Create Oneflow contract" }}
                          >
                            <AppIcon of='create' />
                          </AppIconButton>
                        }
                        subProps={{
                          contract: contract,
                          onCancel: noop,
                          onSave: (newValue) => {
                            onUpdate(newValue);
                          },
                        }}
                      />
                    ) : (
                      <OneflowContractRefDisplayModal
                        trigger={
                          <AppIconButton
                            variant='text'
                            size='small'
                            color='default'
                            tooltipProps={{ title: "Quick view Oneflow contract info" }}
                          >
                            <AppIcon of='quickView' />
                          </AppIconButton>
                        }
                        subProps={{
                          contractRef: contract.oneflowContract,
                        }}
                      />
                    )
                  }
                >
                  {!contract.oneflowContract && (
                    <>
                      <AppChip
                        variant='outlined'
                        size='small'
                        color='default'
                        icon={<AppIcon of={"cross"} />}
                        label='Not created'
                      />
                    </>
                  )}
                  {contract.oneflowContract && (
                    <>
                      <AppChip
                        variant='outlined'
                        size='small'
                        color='default'
                        icon={<AppIcon of={"check"} />}
                        label='Created'
                      />{" "}
                      <AppLink
                        to={undefined}
                        href={contract.oneflowContract.contractUrl}
                        target='_blank'
                      >
                        Open <AppIcon of='openInNew' inText />
                      </AppLink>
                    </>
                  )}
                </FieldValue>
              )}
            </Box>

            {!isEmpty(contract.attachments) && (
              <Box>
                <FileListView
                  files={FileItem.createManyFrom(contract.attachments)}
                  maxVisibleFiles={10}
                  itemWidth={80}
                  itemHeight={80}
                />
              </Box>
            )}
          </Stack>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.Contract}
          entityId={contract.id}
          entity={contract}
          entityUpdatePermission={AppPermission.ContractManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={onUpdate}
        />

        {/* Stages */}
        <ViewContentBlock spacing={2}>
          <Box sx={{ my: 2 }}>
            <ViewContentBlockHeader>Stages</ViewContentBlockHeader>

            <GeneralStageHistory
              stageEnumType='ContractStage'
              stageHistory={contract.stageHistory}
              withUpdateButtons
              onUpdateStage={async (newStage) => await handleUpdateStage(newStage)}
            />
          </Box>
        </ViewContentBlock>

        {contract.settings?.isCheckEnabled && (
          <>
            {/* Check out spot info */}
            <ViewContentBlock>
              <ContractSpotInfoDisplay
                contract={contract}
                spotInfoType='CheckOut'
                action={
                  <Stack
                    direction={{ xxs: "column", md: "row" }}
                    sx={{ alignItems: { xxs: "flex-start", md: "center" }, gap: 1 }}
                  >
                    {contract.checkOutSpotInfo?.visualInspectionId &&
                      contract.checkInSpotInfo?.visualInspectionId && (
                        <Button
                          variant='outlined'
                          size='small'
                          color='text'
                          startIcon={<AppIcon of='compare' />}
                          onClick={() => {
                            setIsCompareVisualInspectionsModalOpen(true);
                          }}
                        >
                          Compare photos
                        </Button>
                      )}

                    <Button
                      variant='outlined'
                      size='small'
                      color='text'
                      startIcon={<AppIcon of='edit' />}
                      onClick={() => setIsEditCheckOutSpotInfoModalOpen(true)}
                    >
                      Edit
                    </Button>
                  </Stack>
                }
                onUpdate={onUpdate}
              />
            </ViewContentBlock>

            {/* Check in spot info */}
            <ViewContentBlock>
              <ContractSpotInfoDisplay
                contract={contract}
                spotInfoType='CheckIn'
                action={
                  <Stack
                    direction={{ xxs: "column", md: "row" }}
                    sx={{ alignItems: { xxs: "flex-start", md: "center" }, gap: 1 }}
                  >
                    {contract.checkOutSpotInfo?.visualInspectionId &&
                      contract.checkInSpotInfo?.visualInspectionId && (
                        <Button
                          variant='outlined'
                          size='small'
                          color='text'
                          startIcon={<AppIcon of='compare' />}
                          onClick={() => {
                            setIsCompareVisualInspectionsModalOpen(true);
                          }}
                        >
                          Compare photos
                        </Button>
                      )}

                    <Button
                      variant='outlined'
                      size='small'
                      color='text'
                      startIcon={<AppIcon of='edit' />}
                      onClick={() => setIsEditCheckInSpotInfoModalOpen(true)}
                    >
                      Edit
                    </Button>
                  </Stack>
                }
                onUpdate={onUpdate}
              />
            </ViewContentBlock>
          </>
        )}

        {/* Accessories */}
        <ViewContentBlock>
          <Stack spacing={2}>
            <ViewContentBlockHeader>Accessories</ViewContentBlockHeader>

            {(!contract?.accessories || contract?.accessories?.length === 0) && <NoDataAlert />}

            <AccessoryGrid accessories={contract?.accessories} sx={{ pl: 2 }} />
          </Stack>
        </ViewContentBlock>

        {/* Customer communication */}
        <ViewContentBlock>
          <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
            <Grid container rowSpacing={1}>
              <Grid size={{ xxs: 12, md: 12 }}>
                <Stack
                  sx={{
                    flexDirection: { xxs: "column", md: "row" },
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <ViewContentBlockHeader>Customer communication</ViewContentBlockHeader>

                  <Stack sx={{ flexDirection: "row", alignSelf: "flex-end", gap: 1 }}>
                    <Button
                      variant='outlined'
                      color='text'
                      size='small'
                      onClick={() => setIsCreateCommunicationEntryModalOpen(true)}
                    >
                      Customer contacted
                    </Button>

                    <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                      <MenuWithTrigger
                        withAuthCloseOnClick
                        trigger={
                          <AppIconButton sx={{ ml: "auto" }}>
                            <AppIcon of='moreVert' />
                          </AppIconButton>
                        }
                      >
                        <MenuItem onClick={() => setIsCommunicationInfoModalOpen(true)}>
                          <ListItemIcon>
                            <AppIcon of='history' fontSize='small' />
                          </ListItemIcon>
                          <ListItemText>View communication entries</ListItemText>
                        </MenuItem>
                      </MenuWithTrigger>
                    </AuthorizedElement>
                  </Stack>
                </Stack>
              </Grid>

              <Grid size={{ xxs: 12, md: 12 }}>
                {contract && (
                  <ContractCustomerCommunicationInfo
                    contract={contract}
                    displayProps={{ overview: true, createNewEntry: false, entries: false }}
                  />
                )}
              </Grid>
            </Grid>
          </AuthorizedElement>
        </ViewContentBlock>

        {/* Reminders */}
        <ViewContentBlock>
          <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
            <Grid container rowSpacing={1}>
              <Grid size={{ xxs: 12, md: 12 }}>
                <ViewContentBlockHeader>Reminders</ViewContentBlockHeader>
              </Grid>
              <Grid size={{ xxs: 12, md: 12 }}>
                {contract && (
                  <ContractReminderSettingsDisplay
                    contract={contract}
                    onUpdate={(newValue) => {
                      onUpdate(newValue.contract);
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </AuthorizedElement>
        </ViewContentBlock>

        {/* Settings */}
        <ViewContentBlock>
          <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
            <Stack spacing={2}>
              <ViewContentBlockHeader>Settings</ViewContentBlockHeader>

              {contract && <ContractSettingsDisplay contract={contract} />}
            </Stack>
          </AuthorizedElement>
        </ViewContentBlock>
      </ViewLayout>

      {/* Update check out/check in spot info */}
      {contract && (
        <ContractUpdateSpotInfoModal
          open={isEditCheckOutSpotInfoModalOpen}
          onClose={() => setIsEditCheckOutSpotInfoModalOpen(false)}
          subProps={{
            contract: contract,
            spotInfo: contract.checkOutSpotInfo,
            spotInfoType: "CheckOut",
            onSave: (newValue) => {
              setIsEditCheckOutSpotInfoModalOpen(false);
              onUpdate(newValue);
            },
          }}
        />
      )}
      {contract && (
        <ContractUpdateSpotInfoModal
          open={isEditCheckInSpotInfoModalOpen}
          onClose={() => setIsEditCheckInSpotInfoModalOpen(false)}
          subProps={{
            contract: contract,
            spotInfo: contract.checkInSpotInfo,
            spotInfoType: "CheckIn",
            onSave: (newValue) => {
              setIsEditCheckInSpotInfoModalOpen(false);
              onUpdate(newValue);
            },
          }}
        />
      )}

      {/* Customer contacted */}
      {contract && (
        <CreateContractCustomerCommunicationEntryModal
          createEntryProps={{
            contractId: contract.id!,
            onSave: () => setIsCreateCommunicationEntryModalOpen(false),
          }}
          open={isCreateCommunicationEntryModalOpen}
          onClose={() => setIsCreateCommunicationEntryModalOpen(false)}
        />
      )}

      {/* Communication info */}
      {contract && (
        <ContractCustomerCommunicationInfoModal
          contractCustomerCommunicationInfoProps={{
            contract: contract,
          }}
          open={isCommunicationInfoModalOpen}
          onClose={() => setIsCommunicationInfoModalOpen(false)}
        />
      )}

      {contract && (
        <ContractVisualInspectionComparisonModal
          open={isCompareVisualInspectionsModalOpen}
          onClose={() => setIsCompareVisualInspectionsModalOpen(false)}
          comparisonProps={{ contract: contract }}
        />
      )}
    </PageTabContent>
  );
}
